import React, { useState, useEffect } from "react";
import { ProgressBar, Table, Container, Row, Spinner } from "react-bootstrap";
import "./statistic.css";
import BackArrow from "../../../../components/backArrow";
import useShopStatistics from "../../../../hooks/useShopStatistics";
import ShareModel from "../../../../components/Sharemodel";

const ShopStatistics = () => {
  const {
    isLoading,
    isError,
    error,
    ads,
    planName,
    postAdsLimit,
    adsUsedCount,
    adsRemaining,
    adsUsedPercentage,
    shareLimit,
    totalShares,
    sharesRemaining,
    sharesUsedPercentage,
    startDate,
    endDate,
    facebookShares,
    otherShares,
    shopid,
  } = useShopStatistics();

  const [realtimeShares, setRealtimeShares] = useState({ facebookShares: null, otherShares: null });
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (!shopid) return;

    const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:8080/api";
    const sseUrl = `${apiBaseUrl.replace(/\/api\/?$/, "")}/api/shop/share-stream/${shopid}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.facebookShares !== undefined && data.otherShares !== undefined) {
          setRealtimeShares({
            facebookShares: data.facebookShares,
            otherShares: data.otherShares,
          });
        }
      } catch (e) {
        console.error("SSE parse error:", e);
      }
    };

    return () => {
      eventSource.close();
    };
  }, [shopid]);

  const activeFacebookShares = realtimeShares.facebookShares !== null ? realtimeShares.facebookShares : (facebookShares || 0);
  const activeOtherShares = realtimeShares.otherShares !== null ? realtimeShares.otherShares : (otherShares || 0);

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center py-5 min-vh-100">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading statistics...</span>
        </Spinner>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-5 text-center">
        <div className="alert alert-danger" role="alert">
          {error?.data?.message || error?.message || "Failed to load shop statistics."}
        </div>
      </Container>
    );
  }

  return (
    <Container style={{ background: "#f8fbff" }}>
      <ShareModel
        show={showShareModal}
        handleClosemodal={() => setShowShareModal(false)}
        handleShowshare={() => { }}
        shopid={shopid}
      />
      <Row className="p-5 pt-0">
        <div className="screenHeight">

          {/* Header */}
          <BackArrow />
          <h4 className="mt-2">Shop Statistics</h4>

          {/* Package */}
          <div className="package-info">
            <span>
              Package active: <strong>{planName}</strong>
            </span>
            <br />
            <span className="date">
              Starting date: <strong>{startDate}</strong> | Ending date:{" "}
              <strong>{endDate}</strong>
            </span>
          </div>

          {/* Progress */}
          <div className="section mt-3">
            <h3 className="section-title">Package Progress Tracker</h3>

            {/* Dynamic Ads Used Tracker */}
            <div className="progress-item">
              <div className="progress-label">Ads Used</div>
              <div className="progress-value">{adsUsedCount}/{postAdsLimit}</div>
              <ProgressBar now={adsUsedPercentage} className="custom-progress" />
              <small className="remaining">{adsRemaining} Remaining</small>
            </div>

            {/* Shared to no of users — profile post shares + external shares vs plan limit */}
            <div className="progress-item">
              <div className="progress-label">Shared to no of users</div>
              <div className="progress-value">
                {totalShares}/{shareLimit}
              </div>
              <ProgressBar now={sharesUsedPercentage} className="custom-progress" />
              <small className="remaining">{sharesRemaining} Remaining</small>
            </div>

            {/* Dynamic Goal Tracker based on Ads Used vs Plan Limit */}
            <div className="progress-item">
              <div className="progress-label">Goal ({adsUsedPercentage}% Reached)</div>
              <div className="progress-value">
                {adsUsedPercentage === 100 ? "Goal Achieved" : "Active Ads Running"}
              </div>
              <ProgressBar now={adsUsedPercentage} className="custom-progress" />
            </div>
          </div>

          {/* Dynamic Ads Performance Table */}
          <div className="section">
            <h3 className="section-title">Ad Performance Table</h3>

            <div className="table-container">
              <Table borderless className="performance-table">
                <thead>
                  <tr>
                    <th>Ad Title</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>Conversion %</th>
                    <th>Date Posted</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {ads.length > 0 ? (
                    ads.map((ad, i) => {
                      const impressions = ad.impressions || 0;
                      const clicks = ad.clicks || 0;

                      const conversion =
                        impressions > 0
                          ? ((clicks / impressions) * 100).toFixed(1)
                          : 0;

                      return (
                        <tr key={i}>
                          <td className="ad-title">{ad.description}</td>
                          <td>{impressions}</td>
                          <td>{clicks}</td>
                          <td>{conversion}%</td>
                          <td>
                            {new Date(ad.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <span className="status-active">
                              {new Date(ad.endDate) > new Date()
                                ? "Active"
                                : "Expired"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>
                        No Ads Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* External Marketing Integration */}
          <div className="section external-section p-3">
            <h3 className="section-title">
              External Marketing Integration
            </h3>

            <div className="external-box">
              <div className="total-share">
                <h2>{(15000 + activeFacebookShares + activeOtherShares).toLocaleString()}</h2>
                <p>Total share</p>
              </div>

              <div className="platform-progress">
                <div className="platform">
                  <span>Facebook ({activeFacebookShares})</span>
                  <ProgressBar now={Math.min(100, 85 + activeFacebookShares)} className="platform-bar fb-bar" />
                </div>
                <div className="platform">
                  <span>Other Platforms ({activeOtherShares})</span>
                  <ProgressBar now={Math.min(100, 30 + activeOtherShares)} className="platform-bar pb-bar" />
                </div>
              </div>

              <div className="upload-area-s">
                <h4>Upload Proof/ScreenShort</h4>
                <p>
                  Drag and drop or browse to upload Images or PDFs.
                </p>
                <button className="upload-btn mt-5" style={{ marginRight: "10px" }} onClick={() => setShowShareModal(true)}>
                  Share Shop
                </button>
                <button className="upload-btn mt-5">
                  Upload File
                </button>
              </div>
            </div>
          </div>

        </div>
      </Row>
    </Container>
  );
};

export default ShopStatistics;